import * as React from 'react';

import {
    faRestroom,
    faCreditCard,
    faUtensils,
    faCameraCctv,
    faPrescriptionBottleMedical,
    faStore,
    faChargingStation,
    faCarWash,
    faGarageCar,
    faWheelchair,
    faCartShopping,
    faGarageEmpty,
    faBagsShopping,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useUpdateParking } from 'src/features/parkingManagement/api/updateParking';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { FEATURES } from 'src/features/parkingManagement/types/parking';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { GeneralBlockSubtitle, GeneralBlockTitle } from 'src/registry/components/blocks/GeneralBlock';
import { toast } from 'src/registry/hooks/UseToast';
import { Checkbox } from 'src/registry/ui/Checkbox';
import { Separator } from 'src/registry/ui/Separator';
import { useTitleStore } from 'src/stores/useTitleStore';
import { generalErrorMessages, generalLabelMessages } from 'src/utils/messages';

const AMENITY_MAP = {
    [FEATURES.RESTROOM]: {
        icon: faRestroom,
        label: parkingManagementMessages.restrooms,
    },
    [FEATURES.UNDERGROUND]: {
        icon: faGarageCar,
        label: parkingManagementMessages.underground,
    },
    [FEATURES.SECURITY]: {
        icon: faCameraCctv,
        label: parkingManagementMessages.security,
    },
    [FEATURES.EV_CHARGER]: {
        icon: faChargingStation,
        label: parkingManagementMessages.EVChargingStation,
    },
    [FEATURES.HANDICAP_SPOTS]: {
        icon: faWheelchair,
        label: parkingManagementMessages.handicapSpots,
    },
    [FEATURES.PHARMACY]: {
        icon: faPrescriptionBottleMedical,
        label: parkingManagementMessages.pharmacy,
    },
    [FEATURES.RESTAURANTS]: {
        icon: faUtensils,
        label: parkingManagementMessages.restaurant,
    },
    [FEATURES.ATM]: {
        icon: faCreditCard,
        label: parkingManagementMessages.atm,
    },
    [FEATURES.GROCERIES]: {
        icon: faCartShopping,
        label: parkingManagementMessages.groceries,
    },
    [FEATURES.CAR_WASH]: {
        icon: faCarWash,
        label: parkingManagementMessages.carWash,
    },
    [FEATURES.SHOPPING_CENTER]: {
        icon: faStore,
        label: parkingManagementMessages.shoppingCenter,
    },
    [FEATURES.COVERED]: {
        icon: faGarageEmpty,
        label: parkingManagementMessages.covered,
    },
    [FEATURES.SHOPPING_MALL]: {
        icon: faBagsShopping,
        label: parkingManagementMessages.shoppingMall,
    },
};

const Amenities = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { data } = useGetParking({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });
    const { setTitle, removeTitle } = useTitleStore();
    const [amenities, setAmenities] = React.useState<FEATURES[]>([]);

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        setAmenities(data?.features);
        return () => removeTitle();
    }, [data?.features, formatMessage]);

    const { mutate: updateAmenities } = useUpdateParking({
        onError: (e) => {
            if (e?.response?.data?.detail) {
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: `${formatMessage(generalLabelMessages.details)}: ${e?.response?.data?.detail}`,
                });
            }
        },
    });

    const handleCheckbox = (value: FEATURES) => {
        const updated = amenities.includes(value) ? amenities.filter((a) => a !== value) : [...amenities, value];

        setAmenities(updated);
        updateAmenities({
            geo_type: data?.geo_type,
            parkingId: parkingId,
            features: updated,
        });
    };

    return (
        <>
            <div className="flex flex-col gap-2">
                <GeneralBlockTitle>{formatMessage(parkingManagementMessages.amenities)}</GeneralBlockTitle>
                <GeneralBlockSubtitle>
                    {formatMessage(parkingManagementMessages.amenitiesSubtitle)}
                </GeneralBlockSubtitle>
            </div>
            <Separator />
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-4">
                {Object.values(FEATURES).map((value: FEATURES) => {
                    return (
                        <div className="flex w-full flex-row items-center justify-start" key={value}>
                            <Checkbox
                                value={value}
                                checked={amenities.includes(value)}
                                onCheckedChange={() => handleCheckbox(value)}
                                className="border-primary-600 size-4 border"
                            />
                            <div className="ml-2 whitespace-nowrap">{formatMessage(AMENITY_MAP[value].label)}</div>
                            <FontAwesomeIcon icon={AMENITY_MAP[value].icon} className="text-primary-600 ml-2" />
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default Amenities;
